import { CheckCircle2, Loader2, MessageCircle, Send, X } from "lucide-react";
import { FormEvent, useEffect, useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/lib/auth-context";

type SubmitState = "idle" | "sending" | "sent" | "error";

type SupportTicketResponse = {
  ok?: boolean;
  ticket_id?: string;
};

function cleanField(value: string, maxLength: number) {
  return value.trim().replace(/\s+/g, " ").slice(0, maxLength);
}

async function submitSupportTicket(input: {
  name: string;
  email: string;
  message: string;
  userID?: string;
  userName?: string;
  userEmail?: string;
}) {
  const response = await fetch("/api/support/messages", {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify({
      name: input.name,
      email: input.email,
      message: input.message,
      page_url: typeof window !== "undefined" ? window.location.href : "",
      user_id: input.userID || "",
      user_name: input.userName || "",
      user_email: input.userEmail || "",
      source: "support_chat_widget",
    }),
  });

  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const body = payload && typeof payload === "object" ? payload as Record<string, unknown> : {};
    throw new Error(
      String(body.error_detail || body.detail || body.error || "Could not send your message right now."),
    );
  }

  return payload as SupportTicketResponse;
}

export function SupportChatWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [state, setState] = useState<SubmitState>("idle");
  const [error, setError] = useState("");
  const [ticketID, setTicketID] = useState("");
  const [lastMessage, setLastMessage] = useState("");

  useEffect(() => {
    if (user?.name) setName((current) => current || user.name);
    if (user?.email) setEmail((current) => current || user.email);
  }, [user?.email, user?.name]);

  const initials = useMemo(() => {
    const source = cleanField(user?.name || name || "Support", 80);
    return source
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "S";
  }, [name, user?.name]);

  const canSubmit = cleanField(email, 180).includes("@") && message.trim().length >= 8 && state !== "sending";

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit) return;

    const nextName = cleanField(name || user?.name || "Customer", 120);
    const nextEmail = cleanField(email || user?.email || "", 180).toLowerCase();
    const nextMessage = message.trim().slice(0, 4000);

    setState("sending");
    setError("");

    try {
      const payload = await submitSupportTicket({
        name: nextName,
        email: nextEmail,
        message: nextMessage,
        userID: user?.id,
        userName: user?.name,
        userEmail: user?.email,
      });
      setTicketID(String(payload.ticket_id || ""));
      setLastMessage(nextMessage);
      setMessage("");
      setState("sent");
    } catch (err) {
      setState("error");
      setError(err instanceof Error ? err.message : "Could not send your message right now.");
    }
  }

  return (
    <div className="fixed bottom-[calc(env(safe-area-inset-bottom)+92px)] right-4 z-[95] flex max-w-[calc(100vw-2rem)] flex-col items-end gap-3 md:bottom-6 md:right-6">
      {open ? (
        <section
          aria-label="Customer support chat"
          className="support-chat-panel flex w-[min(352px,calc(100vw-2rem))] max-h-[min(620px,calc(100dvh-128px))] flex-col overflow-hidden rounded-2xl border hairline text-foreground"
          role="dialog"
        >
          <header className="support-chat-strip flex min-h-14 items-center gap-3 border-b hairline px-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border hairline bg-[var(--genrl-panel-accent)] text-sm font-semibold text-white shadow-[var(--shadow-glass)]">
              G
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold leading-tight">GENRL support</h2>
              <p className="mt-1 truncate text-xs text-muted-foreground">Send us the issue and reply email.</p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="support-chat-field flex h-9 w-9 shrink-0 items-center justify-center rounded-full border hairline text-muted-foreground transition hover:text-foreground"
              aria-label="Close support chat"
              title="Close support chat"
            >
              <X className="h-4 w-4" />
            </button>
          </header>

          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-3">
            <div className="flex items-start gap-2">
              <span className="support-chat-field mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border hairline text-[10px] font-bold text-[var(--genrl-panel-accent)]">
                G
              </span>
              <div className="support-chat-bubble max-w-[82%] rounded-xl border hairline px-3 py-2 text-sm leading-5">
                Hi. Send the issue here and the support team will pick it up.
              </div>
            </div>

            {lastMessage ? (
              <div className="flex justify-end gap-2">
                <div className="support-chat-bubble max-w-[82%] rounded-xl border hairline px-3 py-2 text-sm leading-5">
                  {lastMessage}
                </div>
                <span className="support-chat-field mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border hairline text-[10px] font-bold text-foreground">
                  {initials}
                </span>
              </div>
            ) : null}

            {state === "sent" ? (
              <div className="flex items-start gap-2">
                <span className="support-chat-field mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border hairline text-[10px] font-bold text-[var(--genrl-panel-accent)]">
                  G
                </span>
                <div className="support-chat-bubble max-w-[82%] rounded-xl border hairline px-3 py-2 text-sm leading-5">
                  Got it. Your message is logged{ticketID ? ` as ${ticketID}` : ""}.
                </div>
              </div>
            ) : null}
          </div>

          <form onSubmit={onSubmit} className="support-chat-strip space-y-3 border-t hairline p-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Name"
                aria-label="Your name"
                autoComplete="name"
                maxLength={120}
                className="support-chat-field h-10 border hairline text-sm shadow-none placeholder:text-muted-foreground/70"
              />
              <Input
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Reply email"
                aria-label="Reply email"
                autoComplete="email"
                inputMode="email"
                type="email"
                maxLength={180}
                required
                className="support-chat-field h-10 border hairline text-sm shadow-none placeholder:text-muted-foreground/70"
              />
            </div>

            <Textarea
              value={message}
              onChange={(event) => {
                setMessage(event.target.value);
                if (state === "sent") setState("idle");
                if (error) setError("");
              }}
              placeholder="Tell us what happened..."
              aria-label="Support message"
              maxLength={4000}
              required
              className="support-chat-field min-h-24 resize-none border hairline text-sm shadow-none placeholder:text-muted-foreground/70"
            />

            {state === "error" && error ? (
              <p className="rounded-xl border border-red-200/70 bg-red-50/70 px-3 py-2 text-xs text-red-700 backdrop-blur-md" role="alert">
                {error}
              </p>
            ) : null}

            <div className="flex items-center justify-between gap-3">
              <span className="text-xs text-muted-foreground" aria-live="polite">
                {state === "sent" ? "Sent" : `${Math.max(0, 4000 - message.length)} characters left`}
              </span>
              <Button type="submit" variant="ghost" disabled={!canSubmit} className="support-chat-bubble h-10 rounded-lg border hairline px-4 text-foreground hover:bg-[var(--soft-fill)]">
                {state === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : state === "sent" ? <CheckCircle2 className="h-4 w-4" /> : <Send className="h-4 w-4" />}
                {state === "sending" ? "Sending" : state === "sent" ? "Sent" : "Send"}
              </Button>
            </div>
          </form>
        </section>
      ) : null}

      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        className="glass-strong relative flex h-14 min-w-14 items-center justify-center gap-2 rounded-full border hairline px-4 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 hover:bg-[var(--soft-fill)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2"
        aria-label={open ? "Close support chat" : "Open support chat"}
        title={open ? "Close support chat" : "Open support chat"}
      >
        {open ? <X className="h-5 w-5" /> : <MessageCircle className="h-5 w-5" />}
        <span className="hidden sm:inline">{open ? "Close" : "Support"}</span>
        {!open && state === "sent" ? (
          <span className="absolute right-1 top-1 h-3 w-3 rounded-full border-2 border-white bg-emerald-300" aria-hidden="true" />
        ) : null}
      </button>
    </div>
  );
}
